import React, { useState } from 'react';
import { Page } from '../../../types';
import { PagePreview } from './PagePreview';
import { AICodeGenerationService } from '../../../services/aiCodeGenerationService';
import { Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Box, IconButton } from '@mui/material';
import { AutoAwesome, Close } from '@mui/icons-material';

interface HtmlCssEditTabProps {
  page: Page | null;
  codeContent: string;
  onCodeContentChange: (content: string) => void;
  ctaButtonIds?: string[];
  editState?: any;
}

export const HtmlCssEditTab: React.FC<HtmlCssEditTabProps> = ({
  page,
  codeContent,
  onCodeContentChange,
  ctaButtonIds = [],
  editState
}) => {
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenAiDialog = () => {
    setAiDialogOpen(true);
    setError(null);
  };

  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
    setAiPrompt('');
    setError(null);
  };

  const handleGenerateCode = async () => {
    if (!aiPrompt.trim()) {
      setError('プロンプトを入力してください');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const generatedCode = await AICodeGenerationService.generateCode(
        aiPrompt,
        codeContent || undefined
      );
      
      // 生成されたコードを既存のコードに置き換える（または追加する）
      onCodeContentChange(generatedCode);
      handleCloseAiDialog();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'コード生成に失敗しました');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100%',
      width: '100%'
    }}>
      <div style={{ 
        padding: '8px 16px', 
        height: '56px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <h6 style={{ 
          margin: 0, 
          fontSize: '20px', 
          fontWeight: 500 
        }}>
          HTML/CSS コード
        </h6>
        <Button
          variant="contained"
          startIcon={<AutoAwesome />}
          onClick={handleOpenAiDialog}
          size="small"
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            }
          }}
        >
          AI生成
        </Button>
      </div>
      <div style={{
        padding: '16px',
        flex: 1,
        overflow: 'hidden'
      }}>
        <textarea
          value={codeContent}
          onChange={(e) => onCodeContentChange(e.target.value)}
          placeholder="<!-- HTML/CSSコードを入力してください -->&#10;&lt;style&gt;&#10;body {&#10;  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);&#10;  color: white;&#10;  min-height: 100vh;&#10;  display: flex;&#10;  flex-direction: column;&#10;  justify-content: center;&#10;  align-items: center;&#10;  text-align: center;&#10;  padding: 20px;&#10;}&#10;&#10;h1 {&#10;  font-size: 3rem;&#10;  margin-bottom: 1rem;&#10;  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);&#10;}&#10;&#10;p {&#10;  font-size: 1.2rem;&#10;  opacity: 0.9;&#10;}&#10;&lt;/style&gt;&#10;&#10;&lt;h1&gt;ページタイトル&lt;/h1&gt;&#10;&lt;p&gt;ここにコンテンツを入力してください&lt;/p&gt;&#10;&lt;p&gt;HTML/CSSで自由にカスタマイズできます&lt;/p&gt;"
          style={{
            width: '100%',
            height: '100%',
            fontFamily: 'monospace',
            fontSize: '14px',
            border: '1px solid #c4c4c4',
            borderRadius: '8px',
            padding: '12px',
            resize: 'none',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* AI生成ダイアログ */}
      <Dialog 
        open={aiDialogOpen} 
        onClose={handleCloseAiDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <AutoAwesome sx={{ color: '#6366f1' }} />
              <span>AIでHTML/CSSを生成</span>
            </Box>
            <IconButton
              onClick={handleCloseAiDialog}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={6}
              label="生成したい内容を説明してください"
              placeholder="例: 中央にタイトルを表示してその下に特定のテキストを入れる。中央揃えでタイトルは太文字。背景はグラデーションで、モダンなデザインにする。"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              disabled={isGenerating}
              sx={{ mb: 2 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
                {error}
              </Alert>
            )}
            {isGenerating && (
              <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                <CircularProgress size={20} />
                <span>コードを生成中...</span>
              </Box>
            )}
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
              <strong>ヒント:</strong> 具体的に説明するほど、より良いコードが生成されます。
              例：「中央揃え」「太文字」「グラデーション背景」「レスポンシブ対応」など。
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAiDialog} disabled={isGenerating}>
            キャンセル
          </Button>
          <Button
            onClick={handleGenerateCode}
            variant="contained"
            disabled={isGenerating || !aiPrompt.trim()}
            startIcon={isGenerating ? <CircularProgress size={16} /> : <AutoAwesome />}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              }
            }}
          >
            {isGenerating ? '生成中...' : '生成'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

